import React from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { ServiceSchema } from "@/components/seo/ServiceSchema";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Phone, Clock } from "lucide-react";
import { track } from "@/lib/analytics";

const ZonnepanelenArnhem = () => {
  const handleQuoteRequest = () => {
    track('quote_request', { service: 'zonnepanelen', location: 'arnhem' });
    window.open('https://app.sol-vi.nl/form', '_blank');
  };

  const localBusinessData = {
    name: "SOL-VI Zonnepanelen Arnhem",
    description: "Specialist in zonnepanelen installatie in Arnhem. Erkend installateur met ervaring in de regio Gelderland.",
    url: "https://sol-vi.nl/zonnepanelen-arnhem",
    telephone: "+31 6 55 33 18 70",
    email: "info@sol-vi.nl",
    address: {
      streetAddress: "Hoofdstraat 1",
      addressLocality: "Elst",
      addressRegion: "Gelderland", 
      postalCode: "6661 EA",
      addressCountry: "NL"
    },
    openingHours: ["Mo-Fr 08:00-17:00"],
    priceRange: "€€",
    serviceArea: ["Arnhem", "Elst", "Duiven", "Westervoort", "Gelderland"],
    aggregateRating: {
      ratingValue: 4.9,
      reviewCount: 127
    }
  };

  const serviceData = {
    name: "Zonnepanelen Installatie Arnhem",
    description: "Professionele installatie van zonnepanelen in Arnhem en omgeving. Van advies tot installatie en nazorg.",
    provider: "SOL-VI",
    areaServed: ["Arnhem", "Arnhem-Noord", "Arnhem-Zuid", "Presikhaaf", "Malburgen"],
    serviceType: "Zonnepanelen Installatie",
    url: "https://sol-vi.nl/zonnepanelen-arnhem",
    offers: [
      {
        name: "Standaard Zonnepanelen Pakket",
        description: "Compleet pakket met 12-16 panelen voor gemiddeld huishouden",
        priceRange: "€8.000 - €12.000"
      },
      {
        name: "Premium Zonnepanelen Pakket", 
        description: "Hoogrendement panelen met micro-omvormers",
        priceRange: "€12.000 - €18.000"
      }
    ]
  };

  const breadcrumbData = [
    { name: "Home", url: "https://sol-vi.nl" },
    { name: "Zonnepanelen", url: "https://sol-vi.nl/zonnepanelen" },
    { name: "Arnhem", url: "https://sol-vi.nl/zonnepanelen-arnhem" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Zonnepanelen Arnhem | Erkend Installateur | SOL-VI"
        description="🏠 Zonnepanelen in Arnhem? ✅ Erkend installateur ⚡ Gratis advies en offerte 💰 Subsidie advies 🔧 Vakkundige installatie. Bel: 06-55331870"
        keywords="zonnepanelen arnhem, zonnepanelen installateur arnhem, zonnepanelen kopen arnhem, erkend installateur arnhem gelderland"
        canonical="https://sol-vi.nl/zonnepanelen-arnhem"
        type="website"
      />
      
      <LocalBusinessSchema {...localBusinessData} />
      <ServiceSchema {...serviceData} />
      <BreadcrumbSchema items={breadcrumbData} />
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-primary">Arnhem, Gelderland</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Zonnepanelen Arnhem
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  Erkend installateur van zonnepanelen in Arnhem en omgeving. 
                  Van advies tot installatie - alles uit één hand. Gratis offerte binnen 24 uur.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" onClick={handleQuoteRequest}>
                    Gratis Offerte Arnhem
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31616929110">
                        <Phone className="w-4 h-4 mr-2" />
                        Sven: 06-16929110
                      </a>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31617105786">
                        <Phone className="w-4 h-4 mr-2" />
                        Nikki: 06-17105786
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Waarom SOL-VI in Arnhem?</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                    <span>4.9/5 sterren (127 reviews)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    <span>Installatie binnen 2-4 weken</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span>Lokaal in Gelderland sinds 2019</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Zonnepanelen Service in Arnhem
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Gratis Dakanalyse</CardTitle>
                  <CardDescription>
                    Wij analyseren uw dak in Arnhem en berekenen de optimale panelenconfiguratie
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Onze experts bekijken uw situatie ter plaatse en adviseren over de beste oplossing.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Vakkundige Installatie</CardTitle>
                  <CardDescription>
                    Gecertificeerde installateurs zorgen voor een veilige en efficiënte montage
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Alle werkzaamheden volgens de nieuwste normen en met garantie.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Garantie & Service</CardTitle>
                  <CardDescription>
                    15 jaar productgarantie en 25 jaar prestatiegarantie op alle zonnepanelen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Ook na installatie staan wij voor u klaar met onderhoud en service.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Local Projects Section */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Projecten in Arnhem
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Arnhem-Noord</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    16 panelen, 6.4 kWp systeem
                  </p>
                  <p className="text-sm">
                    "Zeer tevreden met de service van SOL-VI. Professionele installatie 
                    en duidelijke uitleg." - Familie Jansen
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Presikhaaf</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    20 panelen, 8.0 kWp systeem
                  </p>
                  <p className="text-sm">
                    "Snelle installatie en al direct merkbaar verschil in de energierekening." 
                    - Dhr. van der Berg
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Arnhem-Zuid</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    12 panelen, 4.8 kWp systeem
                  </p>
                  <p className="text-sm">
                    "Uitstekend advies en keurige afwerking. Aanrader!" 
                    - Mevr. Pietersen
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Klaar voor Zonnepanelen in Arnhem?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Vraag nu een vrijblijvende offerte aan en ontdek hoeveel u kunt besparen 
              met zonnepanelen op uw dak in Arnhem.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={handleQuoteRequest}>
                Gratis Offerte Aanvragen
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/contact">Contact Opnemen</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ZonnepanelenArnhem;